/**
 * Incorporation DOC (M32014) — Word [[placeholder]] formData 빌더.
 * Section 14 / Section 201 (± attestation) 공통 키 + 이사/멤버 반복.
 */
function trim(v: unknown): string {
  return String(v ?? '').trim();
}

/** Malaysia NRIC 12자리 → XXXXXX-XX-XXXX. 여권 등은 원문. */
function formatIdForDoc(raw: unknown, idType?: unknown): string {
  const s = trim(raw);
  if (!s) return '';
  if (/pass/i.test(trim(idType))) return s;
  const digits = s.replace(/\D/g, '');
  if (digits.length === 12) {
    return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  }
  return s;
}

export interface IncDocMapperInput {
  values: Record<string, any>;
  directorIds: number[];
  memberIds: number[];
  /** Section 201 문서에서 포커스할 이사 행 */
  focusDirectorId?: number | null;
  signersForDoc?: any[];
}

function collectDirectors(v: Record<string, any>, ids: number[]) {
  return ids.map((id, i) => ({
    index: i + 1,
    id,
    name: trim(v[`dir${id}_name`]),
    idType: trim(v[`dir${id}_id_type`]),
    idNo: trim(v[`dir${id}_id_no`]),
    nationality: trim(v[`dir${id}_nationality`]),
    dob: trim(v[`dir${id}_dob`]),
    race: trim(v[`dir${id}_race`]),
    email: trim(v[`dir${id}_email`]),
    address: trim(v[`dir${id}_address`]),
  }));
}

function collectMembers(v: Record<string, any>, ids: number[]) {
  return ids.map((id, i) => ({
    index: i + 1,
    id,
    name: trim(v[`mem${id}_name`]),
    idType: trim(v[`mem${id}_id_type`]),
    idNo: trim(v[`mem${id}_id_no`]),
    nationality: trim(v[`mem${id}_nationality`]),
    race: trim(v[`mem${id}_race`]),
    email: trim(v[`mem${id}_email`]),
    address: trim(v[`mem${id}_address`]),
    classOfShare: trim(v[`mem${id}_class_of_share`]),
    noOfShares: trim(v[`mem${id}_no_of_shares`]),
    pricePerShare: trim(v[`mem${id}_price_per_share`]),
  }));
}

/** 외국인 여부 — attestation 문서 선택용 (Incorporation Pre / entity-data 와 동일) */
export function isForeignDirector(v: Record<string, any>, id: number): boolean {
  const nat = trim(v[`dir${id}_nationality`]);
  const idType = trim(v[`dir${id}_id_type`]);
  if (/pass\s*port|foreign|ppt/i.test(idType)) return true;
  if (!nat) return false;
  const natLower = nat.toLowerCase().replace(/[_\s]/g, '');
  const MALAYSIAN_KEYWORDS = ['malaysian', 'malaysia', 'my'];
  return !MALAYSIAN_KEYWORDS.some((k) => natLower.includes(k));
}

export function buildIncorporationDocFormData(input: IncDocMapperInput): Record<string, any> {
  const v = input.values || {};
  const directors = collectDirectors(v, input.directorIds?.length ? input.directorIds : [1]);
  const members = collectMembers(v, input.memberIds?.length ? input.memberIds : [1]);
  const focusId = input.focusDirectorId ?? null;
  const focus = focusId != null
    ? directors.find((d) => d.id === focusId) || directors[0]
    : directors[0];

  const out: Record<string, any> = {
    company_name: trim(v.company_name),
    proposed_company_name: trim(v.company_name),
    lodging_ref_no: trim(v.lodging_ref_no),
    lodging_reference_no: trim(v.lodging_ref_no),
    purpose: trim(v.purpose),
    company_type: trim(v.company_type),
    company_sub_type: trim(v.company_sub_type),
    business_description: trim(v.business_description),
    registered_address: trim(v.registered_address),
    registered_email: trim(v.registered_email),
    registered_office_no: trim(v.registered_office_no),
    registered_fax_no: trim(v.registered_fax_no),
    business_address: trim(v.business_address),
    business_email: trim(v.business_email),
    business_office_no: trim(v.business_office_no),
    business_fax_no: trim(v.business_fax_no),
    total_no_of_shares: trim(v.total_no_of_shares),
    signer_name: trim(v.signer_name),
    signer_nric_passport_no: trim(v.signer_nric_passport_no),
    declaration_date: trim(v.declaration_date),
    date_of_declaration: trim(v.declaration_date),
  };

  for (let n = 1; n <= 3; n++) {
    out[`business_nature_${n}`] = trim(v[`business_nature_${n}`]);
    out[`msic_code_${n}`] = trim(v[`msic_code_${n}`]);
  }

  // 반복 배열 (Word [[if director]] / [[if member]] — director_name, member_name …)
  out.directors = directors.map((d) => ({
    director_name: d.name,
    name: d.name,
    full_name: d.name,
    director_id_type: d.idType,
    id_type: d.idType,
    director_id_no: d.idNo,
    id_no: d.idNo,
    identification_no: d.idNo,
    director_nationality: d.nationality,
    nationality: d.nationality,
    director_dob: d.dob,
    date_of_birth: d.dob,
    dob: d.dob,
    director_race: d.race,
    race: d.race,
    director_email: d.email,
    email: d.email,
    director_address: d.address,
    address: d.address,
  }));
  out.members = members.map((m) => ({
    member_name: m.name,
    name: m.name,
    full_name: m.name,
    member_id_type: m.idType,
    id_type: m.idType,
    member_id_no: m.idNo,
    id_no: m.idNo,
    identification_no: m.idNo,
    member_nationality: m.nationality,
    nationality: m.nationality,
    member_race: m.race,
    race: m.race,
    member_email: m.email,
    email: m.email,
    member_address: m.address,
    address: m.address,
    member_class_of_share: m.classOfShare,
    class_of_share: m.classOfShare,
    member_no_of_shares: m.noOfShares,
    no_of_shares: m.noOfShares,
    number_of_shares: m.noOfShares,
    member_price_per_share: m.pricePerShare,
    price_per_share: m.pricePerShare,
  }));
  out.shareholders = out.members;

  // 포커스 이사 (Section 201 — 템플릿별 varKey 별칭 폭넓게 채움)
  if (focus) {
    const idNoRaw =
      focus.idNo ||
      trim(v.signer_nric_passport_no) ||
      trim(v[`dir${focus.id}_id_no`]) ||
      '';
    const idNoFmt = formatIdForDoc(idNoRaw, focus.idType);
    const put = (key: string, val: string) => {
      if (val) out[key] = val;
    };

    // 이름
    for (const k of [
      'director_name',
      'director_full_name',
      'name',
      'Name',
      'full_name',
      'FullName',
      'fullname',
      'ind_full_name',
      'contact_name',
      'contactname',
      'DirectorName',
      'Director Name',
    ]) {
      put(k, focus.name);
    }

    // NRIC / Passport — Word 템플릿은 nric|passport|ic_passport_no 등 혼용
    for (const k of [
      'director_nric_or_passport_number',
      'director_id_no',
      'director_1_id',
      'nric_or_passport_number',
      'nric_or_passport',
      'nric_passport_no',
      'nric_passport',
      'nric_no',
      'passport_no',
      'passport_number',
      'NRIC_or_Passport_No',
      'NRIC / Passport No',
      'NRIC / Passport No.',
      'nric',
      'NRIC',
      'passport',
      'Passport',
      'id_no',
      'identification_no',
      'ic_passport',
      'ic_passport_no',
      'ind_ic_passport_no',
      'pt_ic_passport_no',
      'contact_id_no',
      'contactidno',
      'icno',
      'IdNo',
      'IdentificationNo',
    ]) {
      put(k, idNoFmt);
    }

    put('director_nationality', focus.nationality);
    put('nationality', focus.nationality);
    put('Nationality', focus.nationality);
    put('director_email', focus.email);
    put('email', focus.email);
    put('director_address', focus.address);
    put('address', focus.address);
    put('residential_address', focus.address);
    put('director_dob', focus.dob);
    put('date_of_birth', focus.dob);
    put('dob', focus.dob);
    put('director_race', focus.race);
    put('race', focus.race);
    put('director_id_type', focus.idType);
    put('id_type', focus.idType);

    // Section 201 서명란이 signer_* / 포커스 이사
    put('signer_name', focus.name);
    put('signer_nric_passport_no', idNoFmt);
    put('SignerName', focus.name);
    put('SignerNRIC', idNoFmt);

    // live DOC 키와 동일 (dir{N}_*) — 템플릿이 이 키를 쓰는 경우
    if (focus.id != null) {
      put(`dir${focus.id}_name`, focus.name);
      put(`dir${focus.id}_id_no`, idNoFmt);
      put(`dir${focus.id}_nationality`, focus.nationality);
      put(`dir${focus.id}_email`, focus.email);
      put(`dir${focus.id}_address`, focus.address);
      put(`dir${focus.id}_dob`, focus.dob);
      put(`dir${focus.id}_race`, focus.race);
      put(`dir${focus.id}_id_type`, focus.idType);
    }
    // Section 201 단건 문서는 보통 1번 이사 슬롯을 씀 → 포커스 이사를 1번에 미러
    put('director_1_name', focus.name);
    put('director_1_full_name', focus.name);
    put('director_1_id_no', idNoFmt);
    put('dir1_name', focus.name);
    put('dir1_id_no', idNoFmt);
  }

  // flat dir1_* / mem1_* 도 유지 (일부 템플릿)
  for (const d of directors) {
    const i = d.index;
    out[`director_${i}_name`] = d.name;
    out[`director_${i}_full_name`] = d.name;
    out[`director_${i}_id_no`] = d.idNo;
    out[`director_${i}_nationality`] = d.nationality;
  }
  for (const m of members) {
    const i = m.index;
    out[`member_${i}_name`] = m.name;
    out[`member_${i}_full_name`] = m.name;
    out[`member_${i}_id_no`] = m.idNo;
    out[`member_${i}_no_of_shares`] = m.noOfShares;
  }

  if (Array.isArray(input.signersForDoc) && input.signersForDoc.length) {
    out.signers = input.signersForDoc;
  }

  return out;
}
